import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/* ============================================================
   8 OPÇÕES DE LOGO — ConvertPix
   Cada logo é um componente SVG isolado.
   - Aceita "size" (px) e "variant" ("dark" | "light")
   - Cores principais: verde neon #22C55E e variações
   ============================================================ */

const G = "#22C55E"; // verde principal
const G_DARK = "#16A34A";
const G_LIGHT = "#86EFAC";

/* 1. PULSE RECOVERY — pulso/heartbeat (estilo atual evoluído) */
function LogoPulse({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="2" y="2" width="44" height="44" rx="12" fill={G} />
      <path
        d="M10 24 L17 24 L20 14 L26 34 L30 22 L33 26 L38 26"
        stroke="#0A0A0A"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/* 2. BOLT DIAMOND — losango (PIX) com raio dentro (instantâneo) */
function LogoBolt({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect
        x="24"
        y="2"
        width="31.11"
        height="31.11"
        rx="6"
        transform="rotate(45 24 2)"
        fill={G}
      />
      <path
        d="M27 14 L18 26 L23 26 L21 34 L30 22 L25 22 L27 14 Z"
        fill="#0A0A0A"
      />
    </svg>
  );
}

/* 3. CHAT MONEY — bolha de WhatsApp com cifrão R$ */
function LogoChat({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <path
        d="M8 22 C8 12.6 15.6 5 25 5 C34.4 5 42 12.6 42 22 C42 31.4 34.4 39 25 39 L18 39 L10 44 L12 36 C9.5 32.7 8 27.6 8 22 Z"
        fill={G}
      />
      <text
        x="25"
        y="28"
        textAnchor="middle"
        fontFamily="Manrope, sans-serif"
        fontWeight="900"
        fontSize="17"
        fill="#0A0A0A"
        letterSpacing="-1"
      >
        R$
      </text>
    </svg>
  );
}

/* 4. LOOP ARROW — seta circular (recuperar/voltar) */
function LogoLoop({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="2" y="2" width="44" height="44" rx="22" fill="#0A0A0A" stroke={G} strokeWidth="2.5" />
      <path
        d="M34 18 A12 12 0 1 0 35 28"
        stroke={G}
        strokeWidth="3.5"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M34 12 L34 19 L27 19"
        stroke={G}
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="24" cy="24" r="3" fill={G} />
    </svg>
  );
}

/* 5. MONOGRAM CP — letras C e P entrelaçadas (geométrico) */
function LogoMono({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="2" y="2" width="44" height="44" rx="10" fill="#0A0A0A" />
      <path
        d="M30 12 C22 12 16 17.5 16 24 C16 30.5 22 36 30 36"
        stroke={G}
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M22 12 L22 36 M22 12 L31 12 C35 12 38 14.5 38 18 C38 21.5 35 24 31 24 L22 24"
        stroke={G_LIGHT}
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/* 6. HEX TECH — hexágono com pulso (estilo SaaS/tech) */
function LogoHex({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <path
        d="M24 3 L42 13 L42 35 L24 45 L6 35 L6 13 Z"
        fill={G}
        stroke={G_DARK}
        strokeWidth="1.5"
      />
      <path
        d="M11 24 L17 24 L20 17 L26 31 L29 22 L32 25 L37 25"
        stroke="#0A0A0A"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/* 7. ARROW UP COIN — moeda com seta para cima (recuperação $) */
function LogoCoin({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="21" fill={G} />
      <circle cx="24" cy="24" r="16.5" fill="none" stroke="#0A0A0A" strokeWidth="1.5" strokeDasharray="2 2" opacity="0.4" />
      <path
        d="M24 33 L24 16 M17 23 L24 16 L31 23"
        stroke="#0A0A0A"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/* 8. SPARK PIX — abstrato, 4 setas convergindo (multi-canal) */
function LogoSpark({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="2" y="2" width="44" height="44" rx="12" fill="#0A0A0A" />
      <path d="M24 6 L29 18 L24 22 L19 18 Z" fill={G} />
      <path d="M42 24 L30 29 L26 24 L30 19 Z" fill={G_LIGHT} />
      <path d="M24 42 L19 30 L24 26 L29 30 Z" fill={G} />
      <path d="M6 24 L18 19 L22 24 L18 29 Z" fill={G_DARK} />
      <circle cx="24" cy="24" r="3" fill="#0A0A0A" stroke={G} strokeWidth="1.5" />
    </svg>
  );
}

/* ============================================================
   PREMIUM SET — 8 conceitos extras (mais modernos / marcantes)
   ============================================================ */

/* 9. REFRESH DIAMOND — losango PIX + arco circular ao redor (PIX + recovery em um) */
function LogoRefresh({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id="g9" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={G_LIGHT} />
          <stop offset="1" stopColor={G_DARK} />
        </linearGradient>
      </defs>
      <path
        d="M24 4 A20 20 0 1 1 8.5 11"
        stroke="url(#g9)"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M5 4 L9 11 L2 13"
        stroke="url(#g9)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <rect
        x="24"
        y="12"
        width="16.97"
        height="16.97"
        rx="3"
        transform="rotate(45 24 12)"
        fill="url(#g9)"
      />
    </svg>
  );
}

/* 10. CHAT PULSE — bolha WhatsApp com pulso dentro (canal + recovery juntos) */
function LogoChatPulse({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id="g10" x1="0" y1="0" x2="0" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={G_LIGHT} />
          <stop offset="1" stopColor={G} />
        </linearGradient>
      </defs>
      <path
        d="M6 22 C6 12 14 4 24 4 C34 4 42 12 42 22 C42 32 34 40 24 40 L17 40 L8 45 L11 37 C8 33 6 28 6 22 Z"
        fill="url(#g10)"
      />
      <path
        d="M13 24 L18 24 L21 17 L26 31 L29 22 L31 25 L35 25"
        stroke="#0A0A0A"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/* 11. NETWORK MESH — pontos conectados (rede/automação) */
function LogoMesh({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="2" y="2" width="44" height="44" rx="14" fill="#0A0A0A" />
      {/* linhas */}
      <path
        d="M12 14 L24 8 L36 14 L36 28 L24 36 L12 28 Z M24 8 L24 36 M12 14 L36 28 M36 14 L12 28"
        stroke={G_DARK}
        strokeWidth="1"
        opacity="0.5"
        fill="none"
      />
      {/* nodes */}
      <circle cx="24" cy="8" r="3" fill={G} />
      <circle cx="36" cy="14" r="2.5" fill={G} />
      <circle cx="36" cy="28" r="2.5" fill={G} />
      <circle cx="24" cy="36" r="3" fill={G_LIGHT} />
      <circle cx="12" cy="28" r="2.5" fill={G} />
      <circle cx="12" cy="14" r="2.5" fill={G} />
      <circle cx="24" cy="22" r="4" fill={G_LIGHT} stroke={G} strokeWidth="1" />
    </svg>
  );
}

/* 12. GRADIENT ORB — esfera com gradiente + glow (premium) */
function LogoOrb({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <radialGradient id="g12" cx="0.35" cy="0.3" r="0.8">
          <stop offset="0" stopColor="#BBF7D0" />
          <stop offset="0.5" stopColor={G} />
          <stop offset="1" stopColor="#064E3B" />
        </radialGradient>
        <filter id="glow12">
          <feGaussianBlur stdDeviation="1.5" />
        </filter>
      </defs>
      <circle cx="24" cy="24" r="20" fill="url(#g12)" />
      <circle cx="18" cy="18" r="6" fill="white" opacity="0.25" />
      <path
        d="M12 25 L18 25 L21 18 L26 32 L29 23 L31 26 L36 26"
        stroke="#0A0A0A"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity="0.85"
      />
    </svg>
  );
}

/* 13. STACK CUBES — cubos isométricos (fintech/transações empilhadas) */
function LogoStack({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="2" y="2" width="44" height="44" rx="10" fill="#0A0A0A" />
      {/* cubo 1 (base) */}
      <path d="M10 32 L24 39 L38 32 L24 25 Z" fill={G_DARK} />
      <path d="M10 32 L10 26 L24 19 L24 25 Z" fill={G_LIGHT} opacity="0.4" />
      <path d="M38 32 L38 26 L24 19 L24 25 Z" fill={G} opacity="0.7" />
      {/* cubo 2 (topo) */}
      <path d="M14 22 L24 27 L34 22 L24 17 Z" fill={G} />
      <path d="M14 22 L14 16 L24 11 L24 17 Z" fill={G_LIGHT} opacity="0.6" />
      <path d="M34 22 L34 16 L24 11 L24 17 Z" fill={G_DARK} />
      {/* seta para cima */}
      <path d="M24 14 L24 6 M21 9 L24 6 L27 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

/* 14. NEON OUTLINE — quadrado neon com C/wave dentro (y2k/cyber) */
function LogoNeon({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <filter id="glow14" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <rect x="2" y="2" width="44" height="44" rx="10" fill="#0A0A0A" />
      <rect
        x="6"
        y="6"
        width="36"
        height="36"
        rx="8"
        fill="none"
        stroke={G}
        strokeWidth="2"
        filter="url(#glow14)"
      />
      <path
        d="M32 16 C26 12 20 14 17 18 C13 23 14 30 19 33 C24 36 30 35 33 30"
        stroke={G_LIGHT}
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
        filter="url(#glow14)"
      />
      <circle cx="33" cy="30" r="2" fill={G_LIGHT} filter="url(#glow14)" />
    </svg>
  );
}

/* 15. BRUTALIST C — C tipográfico em corte negativo (modern/bold) */
function LogoBrutal({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="2" y="2" width="44" height="44" rx="6" fill={G} />
      <path
        d="M36 14 L36 22 L26 22 L26 26 L36 26 L36 34 L18 34 L18 14 Z"
        fill="#0A0A0A"
      />
      <rect x="30" y="6" width="4" height="4" fill="#0A0A0A" />
      <rect x="38" y="38" width="4" height="4" fill="#0A0A0A" />
    </svg>
  );
}

/* 16. ARROW GLYPH — glifo abstrato com seta de retorno (PIX + recovery, simbolismo puro) */
function LogoGlyph({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id="g16" x1="2" y1="2" x2="46" y2="46" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={G} />
          <stop offset="1" stopColor={G_DARK} />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="44" height="44" rx="14" fill="url(#g16)" />
      {/* curva grossa que entra e volta */}
      <path
        d="M14 32 Q14 14 28 14 Q38 14 38 22 Q38 28 30 28 L22 28"
        stroke="#0A0A0A"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      {/* ponta de seta */}
      <path
        d="M26 24 L22 28 L26 32"
        stroke="#0A0A0A"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/* ============================================================
   WORDMARK — texto "ConvertPix" reaproveitável
   ============================================================ */
function Wordmark({ variant = "dark", weight = "extrabold" }) {
  const color = variant === "dark" ? "#FFFFFF" : "#0A0A0A";
  return (
    <span
      className={`font-${weight} tracking-tight text-[22px]`}
      style={{ color, fontFamily: "Manrope, sans-serif" }}
    >
      Convert<span style={{ color: G }}>Pix</span>
    </span>
  );
}

/* ============================================================
   Card de exibição
   ============================================================ */
function LogoCard({ id, name, tagline, Icon, recommended }) {
  return (
    <div
      data-testid={`logo-card-${id}`}
      className="group relative rounded-2xl border border-white/10 bg-[#0F0F12] overflow-hidden hover:border-[#22C55E]/40 transition-all"
    >
      {recommended && (
        <span
          data-testid={`logo-recommended-${id}`}
          className="absolute top-3 right-3 z-10 px-2 py-1 rounded-full bg-[#22C55E] text-black text-[10px] font-bold tracking-wider uppercase"
        >
          Recomendado
        </span>
      )}

      {/* Topo — fundo escuro */}
      <div className="px-6 pt-8 pb-6 flex items-center gap-3 bg-gradient-to-br from-[#0A0A0A] via-[#0F0F12] to-[#0A0A0A]">
        <Icon size={56} />
        <Wordmark variant="dark" />
      </div>

      {/* Linha do meio — só ícone */}
      <div className="grid grid-cols-2 border-y border-white/5">
        <div className="py-6 flex flex-col items-center gap-2 bg-[#0A0A0A]">
          <Icon size={48} />
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Dark</span>
        </div>
        <div className="py-6 flex flex-col items-center gap-2 bg-white">
          <Icon size={48} />
          <span className="text-[10px] uppercase tracking-wider text-zinc-400">Light</span>
        </div>
      </div>

      {/* Wordmark em fundo claro */}
      <div className="px-6 py-6 flex items-center gap-3 bg-white">
        <Icon size={36} />
        <Wordmark variant="light" />
      </div>

      {/* Texto descritivo */}
      <div className="px-6 py-5 border-t border-white/5">
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-white font-bold text-base">{name}</h3>
          <span className="text-[11px] text-zinc-600 font-mono">#{String(id).padStart(2, "0")}</span>
        </div>
        <p className="text-sm text-zinc-400 leading-relaxed">{tagline}</p>
      </div>
    </div>
  );
}

/* ============================================================
   Página principal
   ============================================================ */
const LOGOS = [
  {
    id: 1,
    name: "Pulse Recovery",
    tagline: "Pulso vivo dentro do bloco verde - comunica recuperação em tempo real e dinamismo. Estilo SaaS confiável.",
    Icon: LogoPulse,
    recommended: true,
  },
  {
    id: 2,
    name: "Bolt Diamond",
    tagline: "Losango do PIX + raio dentro. Comunica velocidade, instantaneidade e o universo PIX direto.",
    Icon: LogoBolt,
  },
  {
    id: 3,
    name: "Chat Money",
    tagline: "Bolha de WhatsApp com R$ - deixa óbvio o canal (Whats) e o resultado (dinheiro).",
    Icon: LogoChat,
  },
  {
    id: 4,
    name: "Loop Recovery",
    tagline: "Seta circular - metáfora visual de recuperação, retorno do dinheiro perdido. Minimal e elegante.",
    Icon: LogoLoop,
  },
  {
    id: 5,
    name: "Monogram CP",
    tagline: "Letras C + P entrelaçadas. Identidade tipográfica forte, ótimo pra favicon e marca compacta.",
    Icon: LogoMono,
  },
  {
    id: 6,
    name: "Hex Tech",
    tagline: "Hexágono com pulso. Vibe tech/infra - passa autoridade técnica, ideal pra audiência avançada.",
    Icon: LogoHex,
  },
  {
    id: 7,
    name: "Coin Up",
    tagline: "Moeda com seta pra cima. Direto ao ponto: dinheiro voltando. Forte apelo financeiro.",
    Icon: LogoCoin,
  },
  {
    id: 8,
    name: "Spark Pix",
    tagline: "Quatro setas convergindo no centro - multi-canal, automação, hub de recovery. Mais abstrato.",
    Icon: LogoSpark,
  },
  {
    id: 9,
    name: "Refresh Diamond",
    tagline: "Losango do PIX envolto por arco de seta - fusão perfeita: PIX + recuperação em UM símbolo. Gradient verde premium.",
    Icon: LogoRefresh,
    recommended: true,
  },
  {
    id: 10,
    name: "Chat Pulse",
    tagline: "Bolha de WhatsApp com pulso vivo dentro - comunica canal e ação numa só leitura. Moderno e direto.",
    Icon: LogoChatPulse,
  },
  {
    id: 11,
    name: "Network Mesh",
    tagline: "Pontos conectados formando uma rede - automação, multi-tenant, integrações. Vibe SaaS infra.",
    Icon: LogoMesh,
  },
  {
    id: 12,
    name: "Gradient Orb",
    tagline: "Esfera com gradiente radial e brilho premium. Look futurista, ideal pra posicionamento high-end.",
    Icon: LogoOrb,
  },
  {
    id: 13,
    name: "Stack Cubes",
    tagline: "Cubos isométricos empilhados com seta pra cima - fintech sério (vibe Stripe/Mercury). Transações ganhando volume.",
    Icon: LogoStack,
  },
  {
    id: 14,
    name: "Neon Outline",
    tagline: "Contorno neon com brilho + curva luminosa dentro. Estética cyber/y2k, ousado e diferente do mercado.",
    Icon: LogoNeon,
  },
  {
    id: 15,
    name: "Brutalist C",
    tagline: "C tipográfico em corte negativo, com marcas registráveis nas bordas. Brutalismo digital, marcante na grid.",
    Icon: LogoBrutal,
  },
  {
    id: 16,
    name: "Arrow Glyph",
    tagline: "Glifo curvado com seta de retorno - simbolismo puro de recuperação. Memorável no silhouette test.",
    Icon: LogoGlyph,
  },
];

export default function LogoOptions() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="logo-options-page">
      {/* Header */}
      <header className="border-b border-white/5 sticky top-0 bg-[#0A0A0A]/80 backdrop-blur-xl z-20">
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="back-to-landing-link"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition"
          >
            <ArrowLeft size={16} />
            Voltar
          </Link>
          <span className="text-xs font-mono text-[#22C55E] tracking-wider">
            _LOGO_OPTIONS / CONVERTPIX
          </span>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-16 max-w-[1400px] mx-auto">
        <div className="max-w-3xl">
          <span
            data-testid="logo-page-eyebrow"
            className="inline-block text-xs font-mono tracking-wider text-[#22C55E] mb-4"
          >
            _ESCOLHA A IDENTIDADE
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5 leading-[1.05]">
            16 conceitos de logo. <br />
            <span className="text-zinc-500">Pra um SaaS que recupera dinheiro.</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-2xl">
            8 do primeiro lote + <span className="text-[#22C55E] font-semibold">8 novos premium</span> -
            mais marcantes, modernos e visualmente fortes. Cada um comunica um aspecto da ConvertPix:{" "}
            <span className="text-white">recovery</span>,{" "}
            <span className="text-white">velocidade</span>,{" "}
            <span className="text-white">WhatsApp</span> ou{" "}
            <span className="text-white">tecnologia</span>. Veja em fundo escuro e claro,
            com e sem o nome. Me diga o número do que mais combina.
          </p>
        </div>
      </section>

      {/* Grid de logos — Lote 1 */}
      <section className="px-6 pb-12 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs font-mono tracking-wider text-zinc-500">_LOTE_01</span>
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-xs text-zinc-500">Conceitos iniciais · 01 → 08</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LOGOS.filter((l) => l.id <= 8).map((l) => (
            <LogoCard key={l.id} {...l} />
          ))}
        </div>
      </section>

      {/* Grid de logos — Lote 2 Premium */}
      <section className="px-6 pb-24 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-6 mt-8">
          <span className="text-xs font-mono tracking-wider text-[#22C55E]">_LOTE_02_PREMIUM</span>
          <div className="h-px flex-1 bg-[#22C55E]/30" />
          <span className="text-xs text-[#22C55E]">Mais marcantes · 09 → 16</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LOGOS.filter((l) => l.id > 8).map((l) => (
            <LogoCard key={l.id} {...l} />
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="px-6 pb-24 max-w-[1400px] mx-auto">
        <div className="rounded-2xl border border-[#22C55E]/30 bg-gradient-to-br from-[#22C55E]/10 via-transparent to-transparent p-8 sm:p-12">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">Qual te chamou mais atenção?</h2>
          <p className="text-zinc-400 mb-6 max-w-2xl">
            Me passa o <span className="text-white font-semibold">número</span> (01 a 16) ou o nome do
            estilo que mais conversa com a marca. Aplico em todo o site, app e favicon - depois ajusto
            cor, peso e variações que precisar.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/"
              data-testid="back-to-landing-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#22C55E] text-black font-semibold hover:bg-[#16A34A] transition"
            >
              <ArrowLeft size={16} />
              Voltar pra landing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
